import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { ChainId, Currency, PriceMapping } from 'utils/types';
import {
  getBscTokenPrices,
  getEthTokenPrices,
  getMaticTokenPrices,
  getXDaiTokenPrices,
} from 'utils/price';

type State = {
  ethTokenPrices: PriceMapping;
  xDaiTokenPrices: PriceMapping;
  bscTokenPrices: PriceMapping;
  maticTokenPrices: PriceMapping;
};

type ContextProps = State & {
  isInitialized: boolean;
  // Ethereum
  ethUsd?: number;
  ethSurfUsd?: number;
  // xDai
  rSurfUsd?: number;
  xDaiSurfUsd?: number;
  // BSC
  bscSurfUsd?: number;
  // Polygon
  maticSurfUsd?: number;
  towelUsd?: number;
  getChainTokenPrices: (chainId?: ChainId) => PriceMapping;
};

const PriceContext = createContext({} as ContextProps);

export const usePrice = () => useContext(PriceContext);

export const PriceProvider: React.FC = (props) => {
  const [isInitialized, setInitialized] = useState(false);
  const [{ ethTokenPrices, xDaiTokenPrices, bscTokenPrices, maticTokenPrices }, setTokenPrices] =
    useState<State>({
      ethTokenPrices: {},
      xDaiTokenPrices: {},
      bscTokenPrices: {},
      maticTokenPrices: {},
    });

  const getChainTokenPrices = useCallback(
    (chainId?: ChainId) => {
      switch (chainId) {
        case ChainId.XDai:
          return xDaiTokenPrices;
        case ChainId.BscMainnet:
          return bscTokenPrices;
        case ChainId.MaticMainnet:
          return maticTokenPrices;
        default:
          return ethTokenPrices;
      }
    },
    [ethTokenPrices, xDaiTokenPrices, bscTokenPrices, maticTokenPrices]
  );

  useEffect(() => {
    const fetchTokenPrices = async () => {
      try {
        const [nextEthTokenPrices, nextXDaiTokenPrices, nextBscTokenPrices, nextMaticTokenPrices] =
          await Promise.all([
            getEthTokenPrices(),
            getXDaiTokenPrices(),
            getBscTokenPrices(),
            getMaticTokenPrices(),
          ]);
        setTokenPrices((currentTokenPrices) => ({
          ...currentTokenPrices,
          ethTokenPrices: nextEthTokenPrices,
          xDaiTokenPrices: nextXDaiTokenPrices,
          bscTokenPrices: nextBscTokenPrices,
          maticTokenPrices: nextMaticTokenPrices,
        }));
        setInitialized(true);
      } catch (err) {
        console.error('Error fetching token prices', err);
      }
    };

    fetchTokenPrices();
    if (process.env.NODE_ENV !== 'development') {
      const fetchInterval = setInterval(fetchTokenPrices, 30000);
      return () => clearInterval(fetchInterval);
    }
  }, []);

  return (
    <PriceContext.Provider
      {...props}
      value={{
        isInitialized,
        ethTokenPrices,
        xDaiTokenPrices,
        bscTokenPrices,
        maticTokenPrices,
        ethUsd: ethTokenPrices[Currency.ETH]?.[Currency.USD],
        ethSurfUsd: ethTokenPrices[Currency.SURF]?.[Currency.USD],
        xDaiSurfUsd: xDaiTokenPrices[Currency.SURF]?.[Currency.USD],
        rSurfUsd: xDaiTokenPrices[Currency.RSURF]?.[Currency.USD],
        bscSurfUsd: bscTokenPrices[Currency.SURF]?.[Currency.USD],
        maticSurfUsd: maticTokenPrices[Currency.SURF]?.[Currency.USD],
        towelUsd: maticTokenPrices[Currency.TOWEL]?.[Currency.USD],
        getChainTokenPrices,
      }}
    />
  );
};
